import { supabase } from '../lib/supabase';
import { OrganizationService } from './organization.service';
import type { Vehicle } from '../types/database.types';
import {
  parseVehicleQuery,
  normalizeRegistration,
  normalizeVIN,
  correctVehicleTypos,
  normalizeQuery
} from '../utils/search-utils';

const SUPABASE_PAGE_SIZE = 1000;

export interface VehicleFilters {
  status?: string;
  make?: string;
  model?: string;
  year_min?: number;
  year_max?: number;
  price_min?: number;
  price_max?: number;
  condition?: 'new' | 'used';
  acquisition_method?: string; // consignment, purchase, trade_in
  search?: string;
}

export interface VehicleCreateData {
  vin: string;
  stock_number?: string;
  make: string;
  model: string;
  year: number;
  trim?: string;
  body_type?: string;
  doors?: number;
  seats?: number;
  condition: 'new' | 'used';
  odometer?: number;
  odometer_unit?: 'km' | 'mi';
  exterior_color?: string;
  interior_color?: string;
  registration?: string;
  registration_state?: string;
  rego_expiry?: string;
  engine_size_l?: string;
  engine_type?: string;
  engine_cylinders?: number;
  transmission?: string;
  drivetrain?: string;
  fuel_type?: string;
  cost_price?: number;
  list_price?: number;
  sale_price?: number;
  minimum_price?: number;
  location?: string;
  description?: string;
  standard_features?: any[];
  images?: any[];
}

class VehicleService {
  // Get all vehicles for the current organization
  async getVehicles(filters?: VehicleFilters) {
    try {
      // Get organization_id from cached service (eliminates redundant query)
      const organizationId = await OrganizationService.getOrganizationId();

      // Build query
      let query = supabase
        .from('vehicles')
        .select('*')
        .eq('organization_id', organizationId)
        .neq('status', 'deleted') // Exclude by status
        .order('created_at', { ascending: false });

      // Apply filters
      if (filters) {
        if (filters.status) {
          query = query.eq('status', filters.status);
        }
        if (filters.make) {
          query = query.ilike('make', `%${filters.make}%`);
        }
        if (filters.model) {
          query = query.ilike('model', `%${filters.model}%`);
        }
        if (filters.year_min) {
          query = query.gte('year', filters.year_min);
        }
        if (filters.year_max) {
          query = query.lte('year', filters.year_max);
        }
        if (filters.price_min) {
          query = query.gte('sale_price', filters.price_min);
        }
        if (filters.price_max) {
          query = query.lte('sale_price', filters.price_max);
        }
        if (filters.condition) {
          query = query.eq('condition', filters.condition);
        }
        if (filters.acquisition_method) {
          if (filters.acquisition_method === 'consignment') {
            // For consignment, check if consignor_id is not null
            query = query.not('consignor_id', 'is', null);
          } else {
            query = query.eq('acquisition_method', filters.acquisition_method);
          }
        }
        if (filters.search) {
          const searchQuery = filters.search.trim(); // ALWAYS trim spaces!
          if (!searchQuery) return { data: [], error: null }; // Empty search returns nothing

          const parsed = parseVehicleQuery(searchQuery);
          const searchTerms: string[] = [];

          // Split search into individual words (handles "q7 2016", "red camry", etc.)
          const words = searchQuery.toLowerCase().split(/\s+/).filter(w => w.length > 0);

          // Search each word in make, model, trim, color
          words.forEach(word => {
            // Skip years - we'll handle them separately
            if (!/^(19|20)\d{2}$/.test(word)) {
              searchTerms.push(`make.ilike.%${word}%`);
              searchTerms.push(`model.ilike.%${word}%`);
              searchTerms.push(`trim.ilike.%${word}%`);
              searchTerms.push(`exterior_color.ilike.%${word}%`);
            }
          });

          // Also search full query (for multi-word makes/models)
          searchTerms.push(`make.ilike.%${searchQuery}%`);
          searchTerms.push(`model.ilike.%${searchQuery}%`);
          searchTerms.push(`trim.ilike.%${searchQuery}%`);

          // Always search VIN, registration, stock number
          searchTerms.push(`vin.ilike.%${searchQuery}%`);
          searchTerms.push(`registration.ilike.%${searchQuery}%`);
          searchTerms.push(`stock_number.ilike.%${searchQuery}%`);

          // Apply all search conditions with OR
          query = query.or(searchTerms.join(','));

          // Apply year filter if detected
          if (parsed.year) {
            query = query.eq('year', parsed.year);
          }
        }
      }

      // Paginate through all results (PostgREST caps at 1000)
      let allData: any[] = [];
      let offset = 0;
      let hasMore = true;

      while (hasMore) {
        const { data, error } = await query.range(offset, offset + SUPABASE_PAGE_SIZE - 1);
        if (error) throw error;
        if (!data || data.length === 0) break;
        allData = allData.concat(data);
        hasMore = data.length === SUPABASE_PAGE_SIZE;
        offset += SUPABASE_PAGE_SIZE;
      }

      return { data: allData, error: null };
    } catch (error: any) {
      console.error('Error fetching vehicles:', error);
      return { data: null, error };
    }
  }

  // Get a single vehicle by ID
  async getVehicle(id: string) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('vehicles')
        .select(`
          *,
          consignor:customers!consignor_id(
            id,
            first_name,
            last_name,
            email,
            phone
          )
        `)
        .eq('id', id)
        .single();

      if (error) throw error;

      // Add consignor_name field for easy access
      if (data && data.consignor) {
        data.consignor_name = `${data.consignor.first_name} ${data.consignor.last_name}`;
      }

      return { data, error: null };
    } catch (error: any) {
      console.error('Error fetching vehicle:', error);
      return { data: null, error };
    }
  }

  // Check if stock number already exists
  async checkStockNumberExists(stockNumber: string, excludeVehicleId?: string) {
    try {
      // Get organization_id from cached service (eliminates redundant query)
      const organizationId = await OrganizationService.getOrganizationId();

      // Check for existing stock number in the same organization
      let query = supabase
        .from('vehicles')
        .select('id, stock_number, make, model, year')
        .eq('organization_id', organizationId)
        .eq('stock_number', stockNumber)
        .is('deleted_at', null);

      // Exclude current vehicle if updating
      if (excludeVehicleId) {
        query = query.neq('id', excludeVehicleId);
      }

      const { data, error } = await query;

      if (error) throw error;

      return {
        exists: data && data.length > 0,
        existingVehicle: data && data.length > 0 ? data[0] : null
      };
    } catch (error: any) {
      console.error('Error checking stock number:', error);
      return { exists: false, existingVehicle: null };
    }
  }

  // Create a new vehicle
  async createVehicle(vehicleData: VehicleCreateData) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Get organization_id from cached service (eliminates redundant query)
      const organizationId = await OrganizationService.getOrganizationId();

      // Check for duplicate stock number if provided
      if (vehicleData.stock_number) {
        const { exists, existingVehicle } = await this.checkStockNumberExists(vehicleData.stock_number);
        if (exists) {
          return {
            data: null,
            error: new Error(
              `Stock number "${vehicleData.stock_number}" already exists for ${existingVehicle?.year} ${existingVehicle?.make} ${existingVehicle?.model}`
            )
          };
        }
      }

      // Prepare vehicle data
      const vehicle = {
        ...vehicleData,
        organization_id: organizationId,
        created_by: user.id,
        standard_features: vehicleData.standard_features || [],
        images: vehicleData.images || [],
        status: vehicleData.status || 'available'
      };

      const { data, error } = await supabase
        .from('vehicles')
        .insert([vehicle])
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error: any) {
      console.error('Error creating vehicle:', error);
      return { data: null, error };
    }
  }

  // Update a vehicle
  async updateVehicle(id: string, updates: Partial<VehicleCreateData>) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Check for duplicate stock number if being updated
      if (updates.stock_number) {
        const { exists, existingVehicle } = await this.checkStockNumberExists(updates.stock_number, id);
        if (exists) {
          return {
            data: null,
            error: new Error(
              `Stock number "${updates.stock_number}" already exists for ${existingVehicle?.year} ${existingVehicle?.make} ${existingVehicle?.model}`
            )
          };
        }
      }

      // Get current vehicle to check if it's soft-deleted
      const { data: currentVehicle } = await supabase
        .from('vehicles')
        .select('status, deleted_at')
        .eq('id', id)
        .single();

      // Prepare update object
      const updateData = {
        ...updates,
        updated_by: user.id,
        updated_at: new Date().toISOString()
      };

      // If vehicle was soft-deleted, restore it
      if (currentVehicle?.deleted_at || currentVehicle?.status === 'deleted') {
        updateData.deleted_at = null;
        updateData.status = 'available'; // Restore to available status
      }

      const { data, error } = await supabase
        .from('vehicles')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error: any) {
      console.error('Error updating vehicle:', error);
      return { data: null, error };
    }
  }

  // Search vehicle by VIN
  async searchByVIN(vin: string) {
    try {
      // Get organization_id from cached service (eliminates redundant query)
      const organizationId = await OrganizationService.getOrganizationId();

      const { data, error } = await supabase
        .from('vehicles')
        .select('*')
        .eq('organization_id', organizationId)
        .eq('vin', vin);

      if (error) throw error;
      return { data, error: null };
    } catch (error: any) {
      console.error('Error searching vehicle by VIN:', error);
      return { data: null, error };
    }
  }

  // Update vehicle status
  async updateVehicleStatus(id: string, status: string) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Get current vehicle data and check for active deals
      const { data: vehicle } = await supabase
        .from('vehicles')
        .select('status')
        .eq('id', id)
        .single();

      if (!vehicle) throw new Error('Vehicle not found');

      // Check if trying to manually set a deal-driven status
      const dealDrivenStatuses = ['on_offer', 'sold'];

      if (dealDrivenStatuses.includes(status)) {
        // Check for active deals (including won deals for sold status)
        const { data: activeDeals } = await supabase
          .from('deals')
          .select('id, stage, status')
          .eq('vehicle_id', id)
          .in('stage', ['proposal', 'won']);

        if (activeDeals && activeDeals.length > 0) {
          throw new Error(
            `Cannot manually set status to "${status}". Vehicle has an active deal. Please manage status through the deal flow.`
          );
        }

        // If trying to set deal-driven status without active deal, suggest creating a deal
        throw new Error(
          `Cannot manually set status to "${status}". Create a ${
            status === 'on_offer' ? 'Proposal' : 'Deal'
          } instead.`
        );
      }

      // Check if vehicle has active deal and prevent changing from deal-driven status
      if (dealDrivenStatuses.includes(vehicle.status)) {
        const { data: activeDeals } = await supabase
          .from('deals')
          .select('id, stage, deal_number')
          .eq('vehicle_id', id)
          .in('stage', ['proposal', 'won']);

        if (activeDeals && activeDeals.length > 0) {
          throw new Error(
            `Vehicle has an active deal (${activeDeals[0].deal_number}). Search for it in Sales to cancel it first.`
          );
        }
      }

      const updates: any = {
        status,
        updated_by: user.id,
        updated_at: new Date().toISOString()
      };

      // Set sold_at if status is 'sold'
      if (status === 'sold') {
        updates.sold_at = new Date().toISOString();
      }

      const { data, error } = await supabase
        .from('vehicles')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error: any) {
      console.error('Error updating vehicle status:', error);
      return { data: null, error };
    }
  }

  // Delete a vehicle (soft delete by setting status to 'deleted')
  async deleteVehicle(id: string) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('vehicles')
        .update({
          status: 'deleted',
          deleted_at: new Date().toISOString(),
          updated_by: user.id
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error: any) {
      console.error('Error deleting vehicle:', error);
      return { data: null, error };
    }
  }

  // Get archived/deleted vehicles (trash bin)
  async getArchivedVehicles() {
    try {
      // Get organization_id from cached service (eliminates redundant query)
      const organizationId = await OrganizationService.getOrganizationId();

      // Get vehicles deleted within the last 30 days
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      const { data, error } = await supabase
        .from('vehicles')
        .select('*')
        .eq('organization_id', organizationId)
        .not('deleted_at', 'is', null)
        .gte('deleted_at', thirtyDaysAgo.toISOString())
        .order('deleted_at', { ascending: false });

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error: any) {
      console.error('Error fetching archived vehicles:', error);
      return { data: [], error };
    }
  }

  // Restore a deleted vehicle
  async restoreVehicle(id: string) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('vehicles')
        .update({
          status: 'available',
          deleted_at: null,
          updated_by: user.id
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error: any) {
      console.error('Error restoring vehicle:', error);
      return { data: null, error };
    }
  }

  // Permanently delete a vehicle
  async permanentlyDeleteVehicle(id: string) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('vehicles')
        .delete()
        .eq('id', id);

      if (error) throw error;
      return { data: { success: true }, error: null };
    } catch (error: any) {
      console.error('Error permanently deleting vehicle:', error);
      return { data: null, error };
    }
  }

  // Auto-cleanup: Delete vehicles that have been in trash for > 30 days
  async cleanupOldDeletedVehicles() {
    try {
      // Get organization_id from cached service (eliminates redundant query)
      const organizationId = await OrganizationService.getOrganizationId();

      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      const { error } = await supabase
        .from('vehicles')
        .delete()
        .eq('organization_id', organizationId)
        .not('deleted_at', 'is', null)
        .lt('deleted_at', thirtyDaysAgo.toISOString());

      if (error) throw error;
      return { data: { success: true }, error: null };
    } catch (error: any) {
      console.error('Error cleaning up old deleted vehicles:', error);
      return { data: null, error };
    }
  }

  // Get vehicle statistics for dashboard
  async getVehicleStats() {
    try {
      const organizationId = await OrganizationService.getOrganizationId();

      // Paginate through all status values (only fetching status column = tiny payload)
      let allStatuses: string[] = [];
      let offset = 0;
      let hasMore = true;

      while (hasMore) {
        const { data, error } = await supabase
          .from('vehicles')
          .select('status')
          .eq('organization_id', organizationId)
          .is('deleted_at', null)
          .neq('status', 'deleted')
          .range(offset, offset + SUPABASE_PAGE_SIZE - 1);

        if (error) throw error;
        if (!data || data.length === 0) break;
        allStatuses = allStatuses.concat(data.map(v => v.status));
        hasMore = data.length === SUPABASE_PAGE_SIZE;
        offset += SUPABASE_PAGE_SIZE;
      }

      const stats = {
        total: allStatuses.length,
        available: allStatuses.filter(s => s === 'available').length,
        sold: allStatuses.filter(s => s === 'sold').length,
        on_offer: allStatuses.filter(s => s === 'on_offer').length,
        in_transit: allStatuses.filter(s => s === 'in_transit').length,
        pending_inspection: allStatuses.filter(s => s === 'pending_inspection').length,
        under_repair: allStatuses.filter(s => s === 'under_repair').length
      };

      return { data: stats, error: null };
    } catch (error: any) {
      console.error('Error fetching vehicle stats:', error);
      return { data: null, error };
    }
  }

  // Upload vehicle images
  async uploadVehicleImage(vehicleId: string, file: File) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Get organization_id from cached service (eliminates redundant query)
      const organizationId = await OrganizationService.getOrganizationId();

      // Generate unique filename with organization path
      const fileExt = file.name.split('.').pop();
      const fileName = `${organizationId}/${vehicleId}/${Date.now()}.${fileExt}`;

      // Upload to Supabase Storage
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('vehicle-images')
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('vehicle-images')
        .getPublicUrl(fileName);

      // Update vehicle with new image
      const { data: vehicle } = await this.getVehicle(vehicleId);
      if (!vehicle) throw new Error('Vehicle not found');

      const currentImages = vehicle.images || [];
      const updatedImages = [...currentImages, publicUrl];

      await this.updateVehicle(vehicleId, { images: updatedImages });

      return { data: publicUrl, error: null };
    } catch (error: any) {
      console.error('Error uploading vehicle image:', error);
      return { data: null, error };
    }
  }

  // Bookmark vehicle for current user
  async bookmarkVehicle(vehicleId: string) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Check if already bookmarked
      const { data: existing } = await supabase
        .from('customer_vehicle_interests')
        .select('id')
        .eq('customer_id', user.id)
        .eq('vehicle_id', vehicleId)
        .single();

      if (existing) {
        return { data: existing, error: null }; // Already bookmarked
      }

      // Add bookmark
      const { data, error } = await supabase
        .from('customer_vehicle_interests')
        .insert([{
          customer_id: user.id,
          vehicle_id: vehicleId,
          interest_type: 'bookmark',
          created_at: new Date().toISOString()
        }])
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error: any) {
      console.error('Error bookmarking vehicle:', error);
      return { data: null, error };
    }
  }

  // Remove bookmark for current user
  async unbookmarkVehicle(vehicleId: string) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('customer_vehicle_interests')
        .delete()
        .eq('customer_id', user.id)
        .eq('vehicle_id', vehicleId)
        .eq('interest_type', 'bookmark');

      if (error) throw error;
      return { data, error: null };
    } catch (error: any) {
      console.error('Error removing bookmark:', error);
      return { data: null, error };
    }
  }

  // Get bookmarked vehicle IDs for current user
  async getBookmarkedVehicles() {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('customer_vehicle_interests')
        .select('vehicle_id')
        .eq('customer_id', user.id)
        .eq('interest_type', 'bookmark');

      if (error) throw error;
      const vehicleIds = data.map(item => item.vehicle_id);
      return { data: vehicleIds, error: null };
    } catch (error: any) {
      console.error('Error fetching bookmarked vehicles:', error);
      return { data: [], error };
    }
  }

  // Generate AI description for advertising
  async generateAIDescription(vehicleId: string, keywords?: string): Promise<string> {
    try {
      // Get vehicle details
      const { data: vehicle, error } = await supabase
        .from('vehicles')
        .select('*')
        .eq('id', vehicleId)
        .single();

      if (error || !vehicle) throw new Error('Vehicle not found');

      // Build a comprehensive description using vehicle data
      const keywordText = keywords ? ` ${keywords}` : '';
      const features = vehicle.standard_features || [];
      const featureText = features.length > 0 ? ` Features: ${features.join(', ')}.` : '';

      const description = `${vehicle.year} ${vehicle.make} ${vehicle.model}${vehicle.trim ? ` ${vehicle.trim}` : ''}.${keywordText} ${vehicle.odometer ? `${vehicle.odometer.toLocaleString()} ${vehicle.odometer_unit || 'km'}.` : ''} ${vehicle.transmission ? `${vehicle.transmission} transmission.` : ''}${vehicle.fuel_type ? ` ${vehicle.fuel_type} fuel.` : ''}${vehicle.exterior_color ? ` ${vehicle.exterior_color} exterior.` : ''}${featureText} ${vehicle.condition === 'used' ? 'Well maintained and in excellent condition.' : 'Brand new condition.'}`;

      return description.trim();
    } catch (error: any) {
      console.error('Error generating AI description:', error);
      throw error;
    }
  }
}

export const vehicleService = new VehicleService();